import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Stack,
  Toolbar,
  IconButton,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material';
import {
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import logodark from "../../assets/images/mAuthenticate-logo.png";
import { useNavigate, Link } from 'react-router-dom';
import NotificationDropdown from "../CommonForBoth/TopbarDropdown/NotificationDropdown";
import ProfileMenu from '../CommonForBoth/TopbarDropdown/ProfileMenu';
import { useOrganizations } from '../../customHooks';

export const Navbar = ({
  showSideBarOption,
  handleSidebarOpen,
  mode,
  toggleMode,
  openSidebar
}) => {
  const navigate = useNavigate();
  const { organization } = useOrganizations();

  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [panelName, setPanelName] = useState("Admin Console");

  useEffect(() => {
    try {
      const raw = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (raw) {
        const userObj = JSON.parse(raw);
        const roleName = userObj.role?.role || userObj.role || userObj.type || userObj.roleId?.role;
        
        if (typeof roleName === 'string') {
          const lowerRole = roleName.toLowerCase();
          if (lowerRole.includes('admin')) {
            setPanelName("Admin Console");
          } else if (lowerRole.includes('user')) {
            setPanelName("User Console");
          } else {
            const formattedRole = roleName.charAt(0).toUpperCase() + roleName.slice(1);
            setPanelName(`${formattedRole} Console`);
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  // flattening logic unchanged…
  const flattenedOptions = organization?.flatMap(cat =>
    cat.options.flatMap(opt => [
      { label: opt.Label, to: opt.to || '', id: opt.id.trim() },
      ...(opt.menu?.flatMap(menu => [
        { label: menu.Label, to: menu.to || '', id: menu.id.trim() },
        ...(menu.children?.map(c => ({ label: c.Label, to: c.to || '', id: c.id.trim() })) || [])
      ]) || [])
    ])
  ) || [];

  const handleInputChange = (_, value) => {
    if (value?.length >= 3) {
      setSearchOptions(flattenedOptions.filter(o =>
        o.label.toLowerCase().includes(value.toLowerCase())
      ));
    } else {
      setSearchOptions([]);
    }
  };

  return (
    <AppBar
      component="nav"
      position="static"
      sx={{
        boxShadow: 'none',
        height: '96px',
        justifyContent: 'center',
        background: mode === 'dark'
          ? '#252b3b'
          : '#0B2545',
        zIndex: 1000,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', height: '96px', minHeight: '96px !important', paddingLeft: openSidebar ? 0 : 2 }}>
        {/* Left Side: Conditionally rendered logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: openSidebar ? '0px' : '200px',
            opacity: openSidebar ? 0 : 1,
            overflow: 'hidden',
            transition: 'width 0.3s ease, opacity 0.3s ease',
          }}
        >
          <Link to="/home" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={logodark}
              alt="Logo Full"
              style={{
                height: "44px",
                maxHeight: "48px",
                width: "auto",
                maxWidth: "190px",
                objectFit: "contain",
              }}
            />
          </Link>
        </Box>

        {/* Center Indicator Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            pl: 4,
            opacity: openSidebar ? 1 : 0,
            visibility: openSidebar ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease, visibility 0.3s ease',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#FFFFFF',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}
          >
            {panelName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">

            <Box textAlign="center">
              <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
                <FullscreenIcon />
              </IconButton>

              {/* Commented out the "Full Screen" text — only the icon is shown now */}
              {/*
              <Typography variant="caption" sx={{ color: 'white', mt: -1 }}>
                Full Screen
              </Typography>
              */}
            </Box>

            <Box textAlign="center">
              <IconButton onClick={toggleMode} sx={{ color: 'white' }}>
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>

              {/* <Typography variant="caption" sx={{ color: 'white', mt: -1 }}>
                {mode === 'dark' ? 'Light' : 'Dark'}
              </Typography> */}
            </Box>

            {/* <IconButton color="inherit">
              <NotificationDropdown />
            </IconButton> */}

            {/* 
              We want only the avatar to show (no username text). 
              The ProfileMenu component is left intact, but wrapped in a container that hides
              likely username elements (common selectors). This preserves the dropdown functionality
              while showing only the picture. If ProfileMenu uses different internal classNames,
              you can update the selectors below inside sx.
            */}
            <Box
              sx={{
                // attempt to hide name/label inside ProfileMenu while keeping avatar & dropdown
                '& .profile-name': { display: 'none !important' }, // common custom class
                '& .MuiTypography-root': { display: 'none !important' }, // MUI typography inside
                '& .username': { display: 'none !important' }, // alternate common class
                // ensure avatar (img or avatar) remains visible
                '& img, & .MuiAvatar-root, & svg': { display: 'inline-block !important' },
              }}
            >
              <ProfileMenu />
            </Box>

          </Stack>
        </Box>
      </Toolbar>

      {isSearchOpen && (
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            top: 96,
            left: 0,
            right: 0,
            backgroundColor: mode === 'dark' ? '#252b3b' : 'white',
            p: 4,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={e => handleInputChange(e, e.target.value)}
          />
        </Box>
      )}
    </AppBar>
  );
};
